import { cn } from "@newxsystems/shared";
import { resolveTootPrimitive } from "../../../../client";
import type { ITootCardProps, TootCardTone } from "./TootCard.props";

const Link = resolveTootPrimitive("Link");

const toneClassName: Record<TootCardTone, string> = {
  accent:
    "border-orange-500/45 bg-orange-500/[0.08] shadow-[0_0_0_1px_rgba(249,115,22,0.16),0_24px_70px_rgba(249,115,22,0.12)]",
  danger:
    "border-red-500/45 bg-red-500/[0.08] shadow-[0_0_0_1px_rgba(239,68,68,0.16),0_24px_70px_rgba(239,68,68,0.12)]",
  default:
    "border-toot-grid-strong/65 bg-[var(--toot-color-surface)] shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_24px_70px_rgba(0,0,0,0.36)]",
  muted:
    "border-toot-grid/75 bg-[var(--toot-color-surface-muted)] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_18px_55px_rgba(0,0,0,0.28)]",
};

export const TootCard = ({
  ariaLabel,
  as = "div",
  body,
  children,
  className,
  footer,
  header,
  href,
  media,
  rel,
  showAccent = true,
  target,
  title,
  tone = "default",
}: ITootCardProps) => {
  const Component = as;
  const content = (
    <Component
      className={cn(
        "group/toot-card relative overflow-hidden rounded-[var(--toot-radius-card)] border text-[var(--toot-color-foreground)] transition-colors duration-300",
        href && "hover:border-orange-400/55 focus-within:border-orange-400/60",
        toneClassName[tone],
        className,
      )}
    >
      {showAccent ? (
        <span className="pointer-events-none absolute left-0 top-0 h-1 w-20 bg-gradient-to-r from-red-500 via-orange-500 to-transparent" />
      ) : null}
      {media ? <div className="border-b border-toot-grid/90">{media}</div> : null}
      {header ? <div className="px-5 pt-5 md:px-6 md:pt-6">{header}</div> : null}
      {body || children ? (
        <div className="px-5 py-5 md:px-6 md:py-6">{body ?? children}</div>
      ) : null}
      {footer ? (
        <div className="border-t border-toot-grid/90 px-5 py-4 md:px-6">{footer}</div>
      ) : null}
    </Component>
  );

  if (!href) {
    return content;
  }

  return (
    <Link
      className="block rounded-[var(--toot-radius-card)] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/80"
      aria-label={ariaLabel}
      href={href}
      rel={rel}
      target={target}
      title={title}
    >
      {content}
    </Link>
  );
};

TootCard.displayName = "Surface::TootCard";
